import type { Control, FieldValues, UseFormRegister, FieldErrors, UseFormWatch, UseFormSetValue } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { TextBoxField, Select, Checkbox, TimeZoneCombobox } from '@/components'
import { WorkspaceSlugField } from './WorkspaceSlugField'
import { WorkspaceDailyLeadsSummaryFields } from './WorkspaceDailyLeadsSummaryFields'
import { WorkspaceStartOfWeekSummaryFields } from './WorkspaceStartOfWeekSummaryFields'
import { fieldLimits } from '@/types/field-limits.generated'
import { isWorkspaceWebsite, websiteHelp, type CompanyWebsite } from './workspaceWebsite'

/**
 * Shared form values for workspace fields.
 * Parent forms should include these fields in their form type.
 */
export interface WorkspaceBaseFormValues {
    Name: string
    Description: string
    Website?: string // Only where the form shows it (see `website`)
    Status: string
    TimeZone: string
    DailyLeadsSummaryEnabled: boolean
    DailyLeadsSummaryHour: number
    WebsterEnabled?: boolean // Edit flow only (see showEditOnlyToggles)
    PostWebinarSummaryEnabled?: boolean
    StartOfWeekSummaryEnabled?: boolean
    StartOfWeekSummaryDayOfWeek?: number
    StartOfWeekSummaryHour?: number
}

/**
 * The subset of the form this component drives through `control` — the two TextBoxFields and the
 * TimeZone Controller. Everything else it renders goes through `register`, which stays untyped.
 *
 * Deliberately narrower than WorkspaceBaseFormValues: AddCustomerModal's workspace form does not
 * carry the daily-summary fields at all, and constraining on fields `control` never touches would
 * lock it out for no benefit.
 */
interface WorkspaceControlledFields {
    Name: string
    Description?: string
    Website?: string
    TimeZone: string
}

interface WorkspaceFormFieldsProps<T extends WorkspaceControlledFields & FieldValues> {
    /**
     * Generic rather than `Control<any>`: since react-hook-form 7.83 `Control` carries a
     * `_options.validate` whose field-name union resolves through the form type, so `Control<any>`
     * no longer accepts a concrete form and `Control<FieldValues>` never did. A type parameter is
     * the only widening that lets all three callers pass their own form shape.
     */
    control: Control<T>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    register: UseFormRegister<any>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    errors: FieldErrors<any>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    watch: UseFormWatch<any>
    /**
     * Required: SlugInput pushes its value back into RHF state via setValue.
     * Without this the slug field stays empty, the hidden required-Slug
     * registration keeps the form invalid, and Save is permanently disabled.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setValue: UseFormSetValue<any>
    /**
     * When provided, the slug input runs a live availability check against
     * this ClientCompanyId. Omit for create-new-company flows where the
     * company and its initial workspace are created in the same request and
     * no collisions are possible (exactly one workspace will exist).
     */
    clientCompanyIdForSlugCheck?: string
    /** Locks the slug field. Set on the edit flow: a workspace slug is fixed at creation. */
    slugLocked?: boolean
    /**
     * Show the Webster Notetaker master switch. Edit flow only: neither create DTO carries it, so a
     * new workspace takes the server-side default (on) and is configured afterwards.
     *
     * The summary-email toggles used to sit behind this flag too. They render in both flows since
     * ORB-1034, so every create caller has to send them or a ticked box is dropped on save.
     */
    showEditOnlyToggles?: boolean
    /**
     * Shows the workspace's own Website, with what a blank one falls back on (ORB-1027). Absent on the new-customer
     * flow: that first workspace is created with the company and uses the company's website.
     */
    website?: CompanyWebsite
}

/**
 * Re-narrows a caller's `control` to the fields this component actually drives.
 *
 * react-hook-form's `Control` is invariant in its form type, so a form that merely *contains* the
 * workspace fields is still not assignable to `Control<WorkspaceControlledFields>`. The constraint
 * on `T` is what guarantees those fields exist; this hands the render body a concrete `Control` so
 * the field names below stay literal and a typo in one is still a compile error.
 *
 * Extracted so the cast has one documented home rather than sitting inside the render body.
 */
const asWorkspaceControl = <T extends WorkspaceControlledFields & FieldValues>(
    control: Control<T>,
): Control<WorkspaceControlledFields> => control as unknown as Control<WorkspaceControlledFields>

const NAME_MAX = 200
const DESC_MAX = 1000
const WEBSITE_MAX = fieldLimits.Workspace.Website.MaxLength

const STATUS_OPTIONS = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
    { value: 'Archived', label: 'Archived' },
    { value: 'Prospect', label: 'Prospect' },
]

/**
 * Shared workspace form fields: Name, Slug, Website (where shown), Description, Status, Time Zone, Daily Leads Summary.
 * Used by WorkspaceCard (edit), WorkspaceEditForm (create), and AddCustomerModal (create).
 *
 * `MyForm` needs at least Name, Description and TimeZone — see WorkspaceControlledFields. The form
 * type is inferred from `control`; nothing here has to be named at the call site.
 *
 * @example
 * ```tsx
 * const { register, control, watch, setValue, formState: { errors } } = useForm<MyForm>()
 * <WorkspaceFormFields control={control} register={register} errors={errors} watch={watch} setValue={setValue} />
 * ```
 */
export function WorkspaceFormFields<T extends WorkspaceControlledFields & FieldValues>({ control, register, errors, watch, setValue, clientCompanyIdForSlugCheck, slugLocked, showEditOnlyToggles, website }: WorkspaceFormFieldsProps<T>) {
    const workspaceControl = asWorkspaceControl(control)

    return (
        <>
            <TextBoxField
                control={workspaceControl}
                name="Name"
                label="Workspace Name"
                required
                showCharCounter
                maxLength={NAME_MAX}
                placeholder="Enter workspace name"
                rules={{
                    required: 'Workspace name is required',
                    maxLength: { value: NAME_MAX, message: `Workspace name cannot exceed ${NAME_MAX} characters.` },
                }}
            />
            <WorkspaceSlugField
                register={register}
                watch={watch}
                setValue={setValue}
                errors={errors}
                slugLocked={slugLocked}
                clientCompanyIdForSlugCheck={clientCompanyIdForSlugCheck}
            />
            {website && (
                <TextBoxField
                    control={workspaceControl}
                    name="Website"
                    label="Website"
                    type="url"
                    maxLength={WEBSITE_MAX}
                    placeholder="https://"
                    helperText={websiteHelp(website)}
                    rules={{
                        maxLength: { value: WEBSITE_MAX, message: `Website cannot exceed ${WEBSITE_MAX} characters.` },
                        validate: value => isWorkspaceWebsite(value ?? '') || 'Enter the full address, starting with http:// or https://, like https://example.com.',
                    }}
                />
            )}
            <TextBoxField
                control={workspaceControl}
                name="Description"
                label="Description"
                multiline
                rows={3}
                showCharCounter
                maxLength={DESC_MAX}
                placeholder="Optional description for this workspace"
                rules={{
                    maxLength: { value: DESC_MAX, message: `Description cannot exceed ${DESC_MAX} characters.` },
                }}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Select
                    label="Status"
                    required
                    options={STATUS_OPTIONS}
                    placeholder="Select status"
                    error={errors.Status?.message as string}
                    {...register('Status', { required: 'Status is required' })}
                />
                <Controller
                    name="TimeZone"
                    control={workspaceControl}
                    rules={{ required: 'Time zone is required' }}
                    render={({ field, fieldState }) => (
                        <TimeZoneCombobox
                            label="Time Zone"
                            required
                            name="TimeZone"
                            value={field.value}
                            onChange={field.onChange}
                            error={fieldState.error?.message as string | undefined}
                        />
                    )}
                />
            </div>
            <WorkspaceDailyLeadsSummaryFields register={register} watch={watch} />
            <Checkbox
                label="Post-Webinar Summary"
                description="Email each web's reviewers and internal team the results once a webinar's attendance has synced — turnout, on-demand signups, standout attendees and poll results. One email per webinar."
                {...register('PostWebinarSummaryEnabled')}
            />
            <WorkspaceStartOfWeekSummaryFields register={register} watch={watch} />
            {showEditOnlyToggles && (
                <Checkbox
                    label="Webster Notetaker"
                    description="Let Webster join Google Meet and Teams meetings in this workspace to record and transcribe them. Turn it off to stop scheduling new meeting bots across the workspace."
                    {...register('WebsterEnabled')}
                />
            )}
        </>
    )
}
